import { FC, useCallback, useMemo } from 'react';
import cx from 'classnames';
import { SvgIcon } from '@/components';
import { useTranslation } from 'react-i18next';
import { useAppSelector } from '@/redux/hooks';
import { paymentSelectors } from '@/redux/selectors';
import { calculateSavePercentage, getPrice } from '@/helpers';
import { Plan } from '@/api';
import styles from './ChecksPriceEntry.module.sass';

interface ChecksPriceEntryProps {
  isActive?: boolean;
  item: Plan;
  onClick: () => void;
}

const ChecksPriceEntry: FC<ChecksPriceEntryProps> = ({
  isActive,
  item,
  onClick,
}) => {
  const { t } = useTranslation();
  const { biggestPricePerCheck, paymentPlans } = useAppSelector(
    paymentSelectors.getPaymentSlice,
  );

  const classes = cx(styles.entry, `${styles['has-discount']}`, {
    [`${styles['is-active']}`]: isActive,
  });

  const { price, pricePerCheck, oldPrice, discount } = getPrice(
    item.price,
    item.value,
    item.old_price,
    0,
  );

  const checksNumber = useMemo(() => Number(item.value), [item.value]);
  const checksText = useMemo(() => {
    return checksNumber === 1
      ? t('translation.checks_without_count_singular')
      : t('translation.checks_without_count_plural');
  }, [t, checksNumber]);

  const savePercentage = useCallback(() => {
    return calculateSavePercentage(
      pricePerCheck,
      biggestPricePerCheck,
      Number(paymentPlans?.baseUsdPrice),
    );
  }, [pricePerCheck, biggestPricePerCheck]);

  return (
    <div className={classes} onClick={onClick}>
      <div className={styles.info}>
        <div className={styles.checks}>
          <SvgIcon name="aml" className={styles.icon} />
          <span>{checksNumber}</span>
          {/* {!!discount && <div className={styles.discount}>-{discount}%</div>} */}
        </div>
        <div className={styles.label}>{checksText}</div>
      </div>

      <div className={styles.price}>
        <div className={styles.priceWrap}>
          {!!savePercentage() && (
            <div className={styles.save}>
              {`${t('translation.safe')} ${savePercentage()}%`}
            </div>
          )}

          <div className={styles.value}>${price}</div>
        </div>

        {!!discount && <div className={styles.oldPrice}>${oldPrice}</div>}

        <div className={styles.of}>
          {`$${pricePerCheck}`}{' '}
          {t('translation.per_check_count_singular', { count: 1 })}
        </div>
      </div>
    </div>
  );
};

ChecksPriceEntry.defaultProps = {
  isActive: false,
};

export default ChecksPriceEntry;
